/**
 * CompanyPicker - Wrapper around generic EntityPicker
 * 
 * Provides company-specific configuration and maintains backward compatibility
 * with any code that imports CompanyPickerField or CompanyPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Company-specific configuration ────────────────────────────────────────────
export const COMPANY_CONFIG = {
  apiEndpoint: "/api/Company/active",
  entityName: "Company",
  idField: "id",
  displayFields: {
    primary: "description"
  },
  tableHeaders: ["Description"],
  tableAccessors: ["description"],
  searchFields: [
    { label: "Description", field: "description", index: 0 },
  ],
  placeholder: "--Select company--"
};

// ── CompanyPickerField wrapper ────────────────────────────────────────────────
/**
 * CompanyPickerField
 * 
 * Usage:
 * <CompanyPickerField
 *   label="Company"
 *   companyId={form.companyId}
 *   displayValue={form.companyDescription}
 *   onSelect={(company) => {
 *     set("companyId", company.id);
 *     set("companyDescription", company.description);
 *   }}
 *   onClear={() => {
 *     set("companyId", "");
 *     set("companyDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function CompanyPickerField({
  companyId,           // company-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={COMPANY_CONFIG}
      entityId={companyId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── CompanyPickerModal wrapper ────────────────────────────────────────────────
/**
 * CompanyPickerModal
 * 
 * Usage (standalone):
 * <CompanyPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(company) => {
 *     set("companyId", company.id);
 *     set("companyDescription", company.description);
 *   }}
 * />
 */
export function CompanyPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={COMPANY_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { CompanyPickerField as default };